/**
 * <utility-search-bar>
 *
 * Hero entry point for the utility rebate search. Behavior depends on
 * whether a `<utility-rebate-list>` exists on the same page:
 *
 *   • Cross-page mode (no list): typing fills the See-all link's `?q=`
 *     query; Enter / submit-button navigates to the merchant-configured
 *     `list_page_url` with `?q=<value>` appended.
 *
 *   • Same-page mode (list found): typing live-filters the list below
 *     once the trimmed value reaches `LIVE_FILTER_MIN_CHARS`, otherwise
 *     it clears the active query so the list reverts to unfiltered.
 *     Enter / submit-button flushes any pending debounce, applies the
 *     same threshold, and smooth-scrolls the grid into view.
 *
 * No autocomplete: the previous panel/listbox flow lived here briefly and
 * was removed once Frank's UX feedback landed (search box widened,
 * see-all link surfaced permanently, results always live on the list page).
 */

import {
  LIVE_FILTER_DEBOUNCE_MS,
  LIVE_FILTER_MIN_CHARS,
} from "@theme/rebate-search-contract";

/**
 * Public surface we depend on from `<utility-rebate-list>`. Defined as a
 * structural type so we don't need to import its class — the hero only
 * cares that `setQuery` exists.
 * @typedef {HTMLElement & {
 *   setQuery?: (query: string, opts?: { scroll?: boolean }) => void;
 * }} RebateListElement
 */

class UtilitySearchBar extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLAnchorElement | null} */
  #seeAll = null;

  /** @type {HTMLButtonElement | null} */
  #submit = null;

  /** @type {string} */
  #listUrl = "";

  /**
   * Same-page list driver. Only assigned once the
   * `<utility-rebate-list>` custom element constructor is registered
   * AND the candidate node is upgraded — see the `whenDefined` block
   * in `connectedCallback`. When `null` (no list on this page, or
   * upgrade pending), the hero falls back to either cross-page
   * navigation or queueing the submit.
   * @type {RebateListElement | null}
   */
  #list = null;

  /**
   * True between `connectedCallback` and the `customElements.whenDefined`
   * resolution when a `<utility-rebate-list>` element is on the page
   * but not yet upgraded. Keeps `#submitSearch` from falling through
   * to navigation during the upgrade race window — navigation here
   * would just refresh the current page.
   * @type {boolean}
   */
  #listUpgradePending = false;

  /**
   * Captures `scroll: true` from a submit that fires while the list
   * is still upgrading, so the `whenDefined` flush honours the user's
   * scroll intent instead of treating the queued apply as a passive
   * live-filter push.
   * @type {boolean}
   */
  #pendingSubmitScroll = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #liveTimer = null;

  /**
   * Lifecycle-scoped controller. The same signal is passed to every
   * `addEventListener` call in `connectedCallback`, so `disconnectedCallback`
   * only has to abort once and every listener is removed in lock-step.
   * Mirrors the pattern in `assets/utility-rebate-search.js`.
   * @type {AbortController | null}
   */
  #lifecycle = null;

  connectedCallback() {
    this.#listUrl = this.dataset.listUrl ?? "";

    this.#input = this.querySelector('[ref="searchInput"]');
    this.#seeAll = this.querySelector('[ref="seeAllLink"]');
    this.#submit = this.querySelector('[ref="searchSubmit"]');

    // Find any list element on the page. PRESENCE is detected
    // synchronously (URL hydration and see-all wiring only need to
    // know "is the list here?"), but treating it as a usable driver
    // is deferred to the `whenDefined` block below — without that
    // gate, `document.querySelector` can return the unupgraded
    // `HTMLElement` during the brief window between markup parse
    // and custom-element registration. A submit during that window
    // would enter the same-page branch (truthy `#list`) but
    // `setQuery?.()` would silently no-op, so nothing would filter
    // and nothing would navigate either.
    const candidate = /** @type {RebateListElement | null} */ (
      document.querySelector("utility-rebate-list")
    );

    // Hydrate the hero input from the URL on the list page so it stays
    // visually in sync with the sidebar input (which `<utility-rebate-list>`
    // hydrates from the same `?q=` param). Only sets when the merchant
    // hasn't pre-filled the input via the editor — empty input takes the
    // URL value.
    if (candidate && this.#input && !this.#input.value) {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q")?.trim() ?? "";
      if (q) this.#input.value = q;
    }

    this.#syncSeeAll();

    this.#lifecycle = new AbortController();
    const { signal } = this.#lifecycle;

    this.#input?.addEventListener(
      "input",
      () => {
        this.#syncSeeAll();
        this.#scheduleLiveFilter();
      },
      { signal },
    );

    this.#input?.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.#submitSearch();
        }
      },
      { signal },
    );

    this.#submit?.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        this.#submitSearch();
      },
      { signal },
    );

    // Same-page driver gate. Two paths:
    //   1. Constructor already registered → element in the DOM is
    //      already upgraded (custom-element upgrade is synchronous on
    //      `customElements.define`), so wire `#list` immediately.
    //   2. Constructor not yet registered → mark the upgrade as
    //      pending and wait. Submits in the meantime are queued via
    //      `#pendingSubmitScroll` so the flush below can honour the
    //      scroll intent. Typing during the race is a no-op (the
    //      `#scheduleLiveFilter` guard skips it) but the flush picks
    //      up whatever's in the input now, so the user's latest input
    //      always reaches the list once it's usable.
    if (candidate) {
      if (customElements.get("utility-rebate-list")) {
        this.#list = candidate;
      } else {
        this.#listUpgradePending = true;
        customElements.whenDefined("utility-rebate-list").then(() => {
          if (!this.isConnected) return;
          this.#list = candidate;
          this.#listUpgradePending = false;
          const scroll = this.#pendingSubmitScroll;
          this.#pendingSubmitScroll = false;
          this.#applyLiveFilter({ scroll });
        });
      }
    }
  }

  disconnectedCallback() {
    this.#lifecycle?.abort();
    this.#lifecycle = null;
    if (this.#liveTimer != null) {
      clearTimeout(this.#liveTimer);
      this.#liveTimer = null;
    }
  }

  /**
   * Keep the always-visible "See all rebates" link in sync with the current
   * input value so navigating to the full list page hydrates the sidebar text
   * search with the same term.
   *
   * URL contract (mirrors `assets/utility-rebate-search.js#scheduleUrlSync`):
   * we ONLY touch the `q` filter param. Anything the merchant configured on
   * `block.settings.list_page_url` (e.g. `?view=utility-rebates-list` or a
   * theme-editor preview token) is preserved exactly.
   *
   * Same-origin URLs emit a pathname-only href so theme-editor previews and
   * relative merchant configs stay portable across preview/production hosts.
   * Cross-origin URLs (an absolute `list_page_url` pointing at another
   * domain) emit the full URL so the rendered link doesn't silently drop
   * the origin and resolve back to the current site.
   */
  #syncSeeAll() {
    if (!this.#seeAll || !this.#listUrl) return;
    const url = this.#buildTargetUrl();
    if (!url) return;
    const sameOrigin = url.origin === window.location.origin;
    const href = sameOrigin
      ? url.pathname + url.search + url.hash
      : url.toString();
    this.#seeAll.setAttribute("href", href);
  }

  /**
   * Build the destination URL for the See all link / search submit button.
   * Returns `null` when no list URL is configured. Sets `q` from the input
   * (or deletes it when empty) and leaves every other param alone.
   * @returns {URL | null}
   */
  #buildTargetUrl() {
    if (!this.#listUrl) return null;
    let url;
    try {
      url = new URL(this.#listUrl, window.location.origin);
    } catch {
      return null;
    }
    const q = this.#input?.value?.trim() ?? "";
    q ? url.searchParams.set("q", q) : url.searchParams.delete("q");
    return url;
  }

  /**
   * Same-page mode only: queue a debounced live-filter push to the list.
   * Below the `LIVE_FILTER_MIN_CHARS` threshold we explicitly clear the
   * active query so deleting characters reverts the list to unfiltered.
   * No-op when there's no list on the page.
   */
  #scheduleLiveFilter() {
    if (!this.#list) return;
    if (this.#liveTimer != null) clearTimeout(this.#liveTimer);
    this.#liveTimer = setTimeout(() => {
      this.#liveTimer = null;
      this.#applyLiveFilter({ scroll: false });
    }, LIVE_FILTER_DEBOUNCE_MS);
  }

  /**
   * Push the current input value into the list, applying the 3+ char
   * guard. `scroll: true` is used by submit (Enter / button) so the
   * results scroll into view; live-filter passes `false`.
   * @param {{ scroll: boolean }} opts
   */
  #applyLiveFilter({ scroll }) {
    if (!this.#list || !this.#input) return;
    const value = this.#input.value.trim();
    const next = value.length >= LIVE_FILTER_MIN_CHARS ? value : "";
    this.#list.setQuery?.(next, { scroll });
  }

  /**
   * Clear the hero input (and see-all `?q=`) when the list clears filters.
   * Does not push another setQuery — the list already owns filter state.
   */
  clearQuery() {
    if (this.#liveTimer != null) {
      clearTimeout(this.#liveTimer);
      this.#liveTimer = null;
    }
    if (this.#input) this.#input.value = "";
    this.#syncSeeAll();
  }

  /**
   * Handle Enter-in-input and the submit button.
   *
   *   • Same-page mode: flush any pending debounce, apply the same 3+
   *     char guard, and scroll the results into view. Sub-threshold
   *     submits still scroll (the user clearly asked to see the list).
   *
   *   • Cross-page mode: navigate to the configured list page with
   *     `?q=<value>`. Empty input no-ops so the action button never
   *     sends users to an unconfigured destination.
   */
  #submitSearch() {
    if (this.#list) {
      if (this.#liveTimer != null) {
        clearTimeout(this.#liveTimer);
        this.#liveTimer = null;
      }
      this.#applyLiveFilter({ scroll: true });
      return;
    }
    // Same-page upgrade race: the list IS on this page but its
    // custom-element definition hasn't landed yet. Queue the scroll
    // intent so the `whenDefined` flush in `connectedCallback` can
    // honour it — falling through to cross-page navigation here would
    // just refresh the current page with the same `?q=` payload.
    if (this.#listUpgradePending) {
      this.#pendingSubmitScroll = true;
      return;
    }

    const q = this.#input?.value?.trim() ?? "";
    if (!q) return;
    const url = this.#buildTargetUrl();
    if (!url) return;
    window.location.assign(url.toString());
  }
}

if (!customElements.get("utility-search-bar")) {
  customElements.define("utility-search-bar", UtilitySearchBar);
}
